<div class="acct-page" [class.acct-admin]="pane === 'admin'">
  <div class="av2-wrap acct">

    <!-- ============ RAIL ============ -->
    <aside class="rail">
      <p class="rail-hd">Your account</p>
      <nav aria-label="Account">
        <ng-container *ngFor="let item of railItems">
          <a
            [routerLink]="item.path"
            [class.active]="pane === item.id"
            [attr.aria-current]="pane === item.id ? 'page' : null"
          >
            {{ item.label }}
            <span class="ct" [class.new]="item.isNew" *ngIf="item.count !== null">{{ item.count }}</span>
          </a>

          <!-- Admin tools appear indented under Admin, only while the Admin
               section is open. Each links to /account/admin?tab=… which the
               admin host reads to select the tool. -->
          <div class="rail-sub" *ngIf="item.id === 'admin' && pane === 'admin' && isAdmin">
            <a
              *ngFor="let t of adminTabs"
              [routerLink]="['/account/admin']"
              [queryParams]="{ tab: t.id }"
              [class.active]="adminTab === t.id"
            >{{ t.label }}<span class="rail-draft" *ngIf="t.draft">Draft</span></a>
            <a *ngIf="editorialEnabled" routerLink="/admin/editorial-intelligence">Editorial Workbench</a>
            <a *ngIf="editorialEnabled" routerLink="/admin/editorial-review">Article Review</a>
            <a *ngIf="editorialEnabled" routerLink="/admin/editorial-workbench">Editorial Inspector</a>
          </div>
        </ng-container>
      </nav>

      <!-- Phone: the admin tools sit BELOW the horizontal tab row as wrapping chips (inside the row
           they made it as tall as the whole tool list, leaving huge gaps). Hidden on desktop. -->
      <div class="rail-tools" *ngIf="pane === 'admin' && isAdmin" aria-label="Admin tools">
        <a
          *ngFor="let t of adminTabs"
          [routerLink]="['/account/admin']"
          [queryParams]="{ tab: t.id }"
          [class.active]="adminTab === t.id"
        >{{ t.label }}<span class="rail-draft" *ngIf="t.draft">Draft</span></a>
        <a *ngIf="editorialEnabled" routerLink="/admin/editorial-intelligence">Editorial Workbench</a>
        <a *ngIf="editorialEnabled" routerLink="/admin/editorial-review">Article Review</a>
        <a *ngIf="editorialEnabled" routerLink="/admin/editorial-workbench">Editorial Inspector</a>
      </div>
    </aside>

    <!-- ============ PANES ============ -->
    <main>
      <ng-container [ngSwitch]="pane">

        <!-- ---------------- OVERVIEW ---------------- -->
        <section class="pane" *ngSwitchCase="'overview'">
          <div class="pane-hd">
            <p class="eyebrow">Your account</p>
            <h1 class="stmt" *ngIf="firstName; else plainGreeting">{{ greeting }}, {{ firstName }}.</h1>
            <ng-template #plainGreeting><h1 class="stmt">{{ greeting }}.</h1></ng-template>
            <p class="lead">Your plates, searches and {{ marketEvents ? 'market updates' : 'alerts' }} in one place.</p>
          </div>

          <div class="tiles">
            <div class="tile">
              <div class="n">{{ summary ? (summary.savedPlates ?? 0) : '–' }}</div>
              <div class="l">Saved plates</div>
              <a class="linkish" routerLink="/account/saved">View saved</a>
            </div>
            <div class="tile">
              <div class="n">{{ summary ? summary.newUpdates : '–' }}</div>
              <div class="l">New updates</div>
              <a class="linkish" routerLink="/account/updates">Open updates</a>
            </div>
            <div class="tile">
              <div class="n">{{ summary ? summary.savedSearches : '–' }}</div>
              <div class="l">Saved searches</div>
              <a class="linkish" routerLink="/account/searches">Manage searches</a>
            </div>
            <div class="tile">
              <div class="n">{{ summary ? summary.followedPlates : '–' }}</div>
              <div class="l">Tracked plates</div>
              <a class="linkish" routerLink="/account/followed">View tracked</a>
            </div>
            <div class="tile">
              <div class="n">–</div>
              <div class="l">My Registrations &middot; est. value</div>
              <a class="linkish" routerLink="/account/registrations">View portfolio</a>
            </div>
          </div>

          <div class="ov-sec">
            <div class="hd"><h2>Latest updates</h2><a class="linkish" routerLink="/account/updates">All updates</a></div>
            <div class="av2-rows" *ngIf="updatesState === 'ready' && overviewUpdates.length > 0; else ovUpdatesEmpty">
              <div class="av2-row" [class.read]="item.readState !== 'new'" *ngFor="let item of overviewUpdates">
                <span class="av2-dot"></span>
                <div>
                  <div class="t"><span class="av2-plate plate-font sm">{{ formatReg(item.registration) }}</span> {{ updateTitle(item) }}</div>
                  <div class="d" *ngIf="updateDetail(item)">{{ updateDetail(item) }}</div>
                  <div class="cause">{{ causeLabel(item) }}</div>
                </div>
                <div class="side">
                  <span>{{ asDate(item.createdAt) | relativeTime }}</span>
                  <a class="linkish" [routerLink]="plateRoute(item.registration)">View plate</a>
                </div>
              </div>
            </div>
            <ng-template #ovUpdatesEmpty>
              <div class="empty"><p>Updates from your saved searches and tracked plates will land here.</p></div>
            </ng-template>
          </div>

          <div class="ov-sec">
            <div class="hd"><h2>Recently viewed</h2><a class="linkish" routerLink="/account/recent">Full history</a></div>
            <div class="recentstrip" *ngIf="recentState === 'ready' && overviewRecent.length > 0; else ovRecentEmpty">
              <a class="ritem" *ngFor="let view of overviewRecent" [routerLink]="plateRoute(view.registration)">
                <span class="av2-plate plate-font">{{ formatReg(view.registration) }}</span>
                <span class="w">{{ asDate(view.viewedAt) | relativeTime }}</span>
              </a>
            </div>
            <ng-template #ovRecentEmpty>
              <div class="empty"><p>Plate pages you open will appear here.</p></div>
            </ng-template>
          </div>

          <div class="ov-sec">
            <div class="hd"><h2>Your tracked plates</h2><a class="linkish" routerLink="/account/followed">All tracked plates</a></div>
            <div class="followrow" *ngIf="followedState === 'ready' && overviewFollowed.length > 0; else ovFollowedEmpty">
              <a class="fcard" *ngFor="let plate of overviewFollowed" [routerLink]="plateRoute(plate.registration)">
                <span class="av2-plate plate-font" [class.muted]="isSold(plate)">{{ formatReg(plate.registration) }}</span>
                <div class="st">{{ intentLabel(plate) }} <span class="av2-chip sold" *ngIf="isSold(plate)">Sold</span></div>
                <div class="chg" *ngIf="marketEvents">{{ latestChangeLine(plate) }}</div>
              </a>
            </div>
            <ng-template #ovFollowedEmpty>
              <div class="empty">
                <p>{{ marketEvents ? 'Track a plate and its market movement will appear here.' : "Track a plate and we'll alert you when it is listed for sale." }}</p>
                <a class="av2-btn av2-btn--ghost av2-btn--sm" routerLink="/plates">Search the register</a>
              </div>
            </ng-template>
          </div>
        </section>

        <!-- ---------------- SAVED SEARCHES ---------------- -->
        <section class="pane" *ngSwitchCase="'searches'">
          <div class="pane-hd">
            <p class="eyebrow">Saved searches</p>
            <div class="ss-hd">
              <h1 class="stmt">Search rules, working for you.</h1>
              <a class="av2-btn av2-btn--green" routerLink="/account/searches/new">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14"></path></svg>
                New saved search
              </a>
            </div>
            <p class="lead" *ngIf="marketEvents">A saved search watches the whole market, auction lots included, for anything matching your criteria, and tells you when something new appears.</p>
            <p class="lead" *ngIf="!marketEvents">A saved search watches for newly listed plates that read as your name, word or initials, and alerts you {{ alertDelivery }}. <a class="linkish" routerLink="/message-hub">Open your messages</a></p>
          </div>

          <div class="skeleton" *ngIf="searchesState === 'loading'" aria-label="Loading saved searches">
            <div class="sk-line"></div><div class="sk-line"></div><div class="sk-line short"></div>
          </div>

          <div class="loaderr" role="alert" *ngIf="searchesState === 'error'">
            <p>We couldn't load your saved searches just now.</p>
            <button class="av2-btn av2-btn--ghost av2-btn--sm" (click)="loadSearches()">Try again</button>
          </div>

          <ng-container *ngIf="searchesState === 'ready'">
            <div class="scard" [class.paused]="search.pausedAt" *ngFor="let search of searches">
              <div>
                <div class="sc-title">
                  <h3>{{ search.plainSummary || search.name }}</h3>
                  <span class="av2-chip new" *ngIf="!search.pausedAt && searchNew[search.id]">{{ searchNew[search.id] }} new</span>
                  <span class="av2-chip quiet" *ngIf="search.pausedAt">Paused</span>
                </div>
                <div class="sum">{{ searchDetails(search) }}</div>
                <div class="al">{{ searchAlertSummary(search) }}</div>
              </div>
              <div class="acts">
                <a class="av2-btn av2-btn--sm" [class.av2-btn--green]="!search.pausedAt" [class.av2-btn--ghost]="search.pausedAt"
                  [routerLink]="searchLink(search)">{{ !search.pausedAt && searchNew[search.id] ? 'View ' + searchNew[search.id] + ' new' : 'View results' }}</a>
                <a class="av2-btn av2-btn--ghost av2-btn--sm" [routerLink]="['/account/searches', search.id, 'edit']">Edit</a>
                <button class="av2-btn av2-btn--green av2-btn--sm" *ngIf="search.pausedAt" (click)="setSearchPaused(search, false)">Resume</button>
                <button class="av2-btn av2-btn--ghost av2-btn--sm" *ngIf="!search.pausedAt" (click)="setSearchPaused(search, true)">Pause</button>
                <ng-container *ngIf="confirmKey === 'search-' + search.id; else searchDeleteAsk">
                  <button class="linkish neg" (click)="deleteSearch(search)">Confirm delete?</button>
                  <button class="linkish quietlink" (click)="cancelConfirm()">Cancel</button>
                </ng-container>
                <ng-template #searchDeleteAsk>
                  <button class="linkish quietlink" (click)="askConfirm('search-' + search.id)">Delete</button>
                </ng-template>
              </div>
            </div>

            <div class="empty" [class.empty--after]="searches.length > 0">
              <p *ngIf="marketEvents">Watch for a kind of plate: pick a style and length, add a word or a budget, and we'll tell you when one comes up. You can also ask Plato, or save any search you run.</p>
              <p *ngIf="!marketEvents">Save a name, word or initials, for any style or just the ones you want, and we'll alert you when a matching plate is listed. You can also save any search you run.</p>
              <div class="starters">
                <a class="av2-btn av2-btn--ghost av2-btn--sm" routerLink="/account/searches/new">Create a saved search</a>
              </div>
            </div>
          </ng-container>
        </section>

        <!-- ---------------- SAVED PLATES (shortlist, userfavourite) ---------------- -->
        <section class="pane" *ngSwitchCase="'saved'">
          <div class="pane-hd">
            <p class="eyebrow">Saved plates</p>
            <h1 class="stmt">Your shortlist.</h1>
            <p class="lead">Plates you've saved to come back to. Saving doesn't send alerts: choose Track plate on any of them to be told {{ marketEvents ? 'when its availability or price changes' : 'when it is listed for sale' }}.</p>
          </div>

          <div class="skeleton" *ngIf="savedState === 'loading'" aria-label="Loading saved plates">
            <div class="sk-line"></div><div class="sk-line"></div><div class="sk-line short"></div>
          </div>

          <div class="loaderr" role="alert" *ngIf="savedState === 'error'">
            <p>We couldn't load your saved plates just now.</p>
            <button class="av2-btn av2-btn--ghost av2-btn--sm" (click)="loadSaved()">Try again</button>
          </div>

          <ng-container *ngIf="savedState === 'ready'">
            <div class="pcard" *ngFor="let plate of saved">
              <div class="pcard-main">
                <div class="pv">
                  <span class="av2-plate plate-font md" [class.muted]="isSold(plate)">{{ plate.formattedRegistration || formatReg(plate.registration) }}</span>
                  <span class="cls">{{ isTracked(plate) ? 'Saved · Tracking' : 'Saved' }}</span>
                </div>
                <div class="mkt">
                  <div class="ev" *ngIf="marketEvents">Latest change: {{ savedChangeLine(plate) }}
                    <span class="av2-chip sold" *ngIf="isSold(plate)">Sold</span>
                  </div>
                  <div class="al" *ngIf="plate.term">Found searching "{{ plate.term }}"</div>
                  <div class="al" *ngIf="plate.savedAt">Saved {{ asDate(plate.savedAt) | date: 'd MMM y' }}</div>
                </div>
                <div class="acts">
                  <a class="av2-btn av2-btn--green av2-btn--sm" [routerLink]="plateRoute(plate.registration)">View plate</a>
                  <button class="av2-btn av2-btn--ghost av2-btn--sm" [disabled]="isTracked(plate)" (click)="trackSaved(plate)">
                    {{ isTracked(plate) ? 'Tracking' : 'Track plate' }}
                  </button>
                  <ng-container *ngIf="confirmKey === 'saved-' + plate.id; else unsaveAsk">
                    <button class="linkish neg" (click)="removeSaved(plate)">Confirm removal?</button>
                    <button class="linkish quietlink" (click)="cancelConfirm()">Cancel</button>
                  </ng-container>
                  <ng-template #unsaveAsk>
                    <button class="linkish quietlink" (click)="askConfirm('saved-' + plate.id)">Remove</button>
                  </ng-template>
                </div>
              </div>
            </div>

            <div class="empty" *ngIf="saved.length === 0">
              <p>You haven't saved any plates yet. Tap the heart on any registration to keep it here.</p>
              <div class="starters">
                <a class="av2-btn av2-btn--ghost av2-btn--sm" routerLink="/plates">Search the register</a>
              </div>
            </div>
          </ng-container>
        </section>

        <!-- ---------------- TRACKED PLATES (followed_plates) ---------------- -->
        <section class="pane" *ngSwitchCase="'followed'">
          <div class="pane-hd">
            <p class="eyebrow">Tracked plates</p>
            <h1 class="stmt">{{ marketEvents ? 'Every change, watched.' : 'Know when it is listed.' }}</h1>
            <p class="lead" *ngIf="marketEvents">Each plate here is monitored for changes in availability and advertised prices. Choose on each plate what you're told about, and how often.</p>
            <p class="lead" *ngIf="!marketEvents">We alert you {{ alertDelivery }} when a plate here is listed for sale. <a class="linkish" routerLink="/message-hub">Open your messages</a></p>
          </div>

          <div class="skeleton" *ngIf="followedState === 'loading'" aria-label="Loading tracked plates">
            <div class="sk-line"></div><div class="sk-line"></div><div class="sk-line short"></div>
          </div>

          <div class="loaderr" role="alert" *ngIf="followedState === 'error'">
            <p>We couldn't load your tracked plates just now.</p>
            <button class="av2-btn av2-btn--ghost av2-btn--sm" (click)="loadFollowed()">Try again</button>
          </div>

          <ng-container *ngIf="followedState === 'ready'">
            <div class="pcard" [class.open]="manageOpenFor === plate.registration" *ngFor="let plate of followed">
              <div class="pcard-main">
                <div class="pv">
                  <span class="av2-plate plate-font md" [class.muted]="isSold(plate)">{{ formatReg(plate.registration) }}</span>
                  <span class="cls">{{ intentLabel(plate) }}</span>
                </div>
                <div class="mkt">
                  <div class="ev" *ngIf="marketEvents">Latest change: {{ latestChangeLine(plate) }}
                    <span class="av2-chip sold" *ngIf="isSold(plate)">Sold</span>
                    <span class="av2-chip est" *ngIf="plate.latestEvent?.provisional">Provisional</span>
                  </div>
                  <div class="al">{{ alertSummary(plate) }}</div>
                  <div class="al">Tracked since {{ asDate(plate.followedAt) | date: 'd MMM y' }}</div>
                </div>
                <div class="acts">
                  <a class="av2-btn av2-btn--green av2-btn--sm" [routerLink]="plateRoute(plate.registration)">View plate</a>
                  <button class="av2-btn av2-btn--ghost av2-btn--sm" *ngIf="canManagePlateAlerts" (click)="manageOpenFor === plate.registration ? closeManageAlerts() : openManageAlerts(plate)">
                    {{ plate.alertTypes.length === 0 ? 'Turn on alerts' : 'Manage alerts' }}
                  </button>
                  <ng-container *ngIf="confirmKey === 'follow-' + plate.id; else unfollowAsk">
                    <button class="linkish neg" (click)="unfollow(plate)">Confirm removal?</button>
                    <button class="linkish quietlink" (click)="cancelConfirm()">Cancel</button>
                  </ng-container>
                  <ng-template #unfollowAsk>
                    <button class="linkish quietlink" (click)="askConfirm('follow-' + plate.id)">Stop tracking</button>
                  </ng-template>
                </div>
              </div>
              <div class="settings" *ngIf="canManagePlateAlerts && manageOpenFor === plate.registration">
                <div class="set-grid">
                  <div>
                    <h4>Tell me about</h4>
                    <label *ngIf="canPlateAlert('available')"><input type="checkbox" [(ngModel)]="alertDraftTypes['available']" /> Becomes available</label>
                    <label *ngIf="canPlateAlert('price')"><input type="checkbox" [(ngModel)]="alertDraftTypes['price']" /> Price changes</label>
                    <label *ngIf="canPlateAlert('new_listings')"><input type="checkbox" [(ngModel)]="alertDraftTypes['new_listings']" /> New dealer listings</label>
                    <label *ngIf="canPlateAlert('sale_result')"><input type="checkbox" [(ngModel)]="alertDraftTypes['sale_result']" /> Sale or auction result</label>
                  </div>
                  <div>
                    <h4>How often</h4>
                    <label *ngIf="canPlateFrequency('instant')"><input type="radio" name="fq" value="instant" [(ngModel)]="alertDraftFrequency" /> Instant</label>
                    <label *ngIf="canPlateFrequency('daily')"><input type="radio" name="fq" value="daily" [(ngModel)]="alertDraftFrequency" /> Daily digest</label>
                    <label *ngIf="canPlateFrequency('weekly')"><input type="radio" name="fq" value="weekly" [(ngModel)]="alertDraftFrequency" /> Weekly digest</label>
                    <label *ngIf="canPlateFrequency('none')"><input type="radio" name="fq" value="none" [(ngModel)]="alertDraftFrequency" /> Account only, no email</label>
                  </div>
                </div>
                <div class="set-foot">
                  <button class="av2-btn av2-btn--green av2-btn--sm" [disabled]="alertSaving" (click)="saveManageAlerts(plate)">Save changes</button>
                  <button class="av2-btn av2-btn--ghost av2-btn--sm" (click)="closeManageAlerts()">Cancel</button>
                  <span class="note">Emails always say why they were sent, and every one carries a pause link.</span>
                </div>
              </div>
            </div>

            <div class="empty" *ngIf="followed.length === 0">
              <p>You aren't tracking any plates yet. Choose Track plate on any registration page and it will be watched for you here.</p>
              <div class="starters">
                <a class="av2-btn av2-btn--ghost av2-btn--sm" routerLink="/plates">Search the register</a>
              </div>
            </div>
          </ng-container>
        </section>

        <!-- ---------------- MY REGISTRATIONS ---------------- -->
        <section class="pane" *ngSwitchCase="'registrations'">
          <div class="pane-hd">
            <p class="eyebrow">My Registrations</p>
            <h1 class="stmt">Your plates, valued.</h1>
            <p class="lead">Registrations you own, tracked against the market. Private to you; nothing here appears on the public register.</p>
          </div>

          <div class="skeleton" *ngIf="registrationsState === 'loading'" aria-label="Loading registrations">
            <div class="sk-line"></div><div class="sk-line"></div><div class="sk-line short"></div>
          </div>

          <div class="loaderr" role="alert" *ngIf="registrationsState === 'error'">
            <p>We couldn't load your registrations just now.</p>
            <button class="av2-btn av2-btn--ghost av2-btn--sm" (click)="loadRegistrations()">Try again</button>
          </div>

          <ng-container *ngIf="registrationsState === 'ready'">
            <div class="folio" *ngIf="registrations.length > 0">
              <div class="b"><div class="n">&ndash;</div><div class="l">Estimated value</div></div>
              <div class="b"><div class="n quietn" *ngIf="portfolioCostPence !== null; else noCost">{{ formatPence(portfolioCostPence) }}</div>
                <ng-template #noCost><div class="n quietn">&ndash;</div></ng-template>
                <div class="l">Recorded cost</div></div>
              <div class="b"><div class="n">&ndash;</div><div class="l">Change vs cost</div></div>
              <div class="note">Estimates appear once the valuation engine is agreed and switched on. Nothing is shown until the data is real. <span class="av2-chip est">Valuation pending</span></div>
            </div>

            <div class="rcard" [class.open]="detailOpenId === view.id" *ngFor="let view of registrations">
              <div class="rcard-main">
                <div class="pv"><span class="av2-plate plate-font md">{{ formatReg(view.registration) }}</span></div>
                <div class="meta">
                  <b>{{ ownershipLabel(view) }}</b>
                  <ng-container *ngIf="view.lastKnownSale">
                    <br />Last known sale {{ formatPence(view.lastKnownSale.pricePence) }} &middot;
                    {{ view.lastKnownSale.saleTypeLabel }}, {{ asDate(view.lastKnownSale.soldDate) | date: 'd MMM y' }}
                    <span *ngIf="view.lastKnownSale.basis === 'all_in_auction'">(all-in)</span>
                  </ng-container>
                </div>
                <div class="est">
                  <div class="v">&ndash;</div>
                  <div class="quietd">Estimate pending</div>
                </div>
                <div class="acts">
                  <button class="av2-btn av2-btn--ghost av2-btn--sm" (click)="toggleDetail(view)">
                    {{ detailOpenId === view.id ? 'Hide details' : 'View details' }}
                  </button>
                </div>
              </div>

              <div class="detail" *ngIf="detailOpenId === view.id">
                <div class="det-grid det">
                  <div>
                    <div class="det-hero">
                      <span class="av2-plate plate-font lg">{{ formatReg(view.registration) }}</span>
                      <div class="vr">&ndash; <span class="av2-chip est">Estimate pending</span></div>
                      <div class="vs">Market estimates appear once the valuation engine is agreed. Nothing is shown until the data is real.</div>
                    </div>
                    <h4>Market history</h4>
                    <div class="timeline" *ngIf="view.lastKnownSale; else noHistory">
                      <div class="ev">Sold {{ formatPence(view.lastKnownSale.pricePence) }} &middot; {{ view.lastKnownSale.saleTypeLabel }}
                        <div class="w">{{ asDate(view.lastKnownSale.soldDate) | date: 'd MMMM y' }} &middot; {{ view.lastKnownSale.basis === 'all_in_auction' ? 'all-in price, fees included' : 'DVLA fixed price' }}</div>
                      </div>
                    </div>
                    <ng-template #noHistory>
                      <div class="placeholder">No verified sale is recorded for this registration yet.</div>
                    </ng-template>
                  </div>
                  <div>
                    <h4 class="h4-first">Your details
                      <button class="linkish editlink" *ngIf="!editingPurchase" (click)="startEditPurchase(view)">Edit</button>
                    </h4>

                    <dl class="dl" *ngIf="!editingPurchase">
                      <div><dt>Purchase date</dt><dd>{{ view.purchaseDate ? (asDate(view.purchaseDate) | date: 'd MMMM y') : 'Not recorded' }}</dd></div>
                      <div><dt>Purchase price</dt><dd>{{ view.purchasePricePence != null ? formatPence(view.purchasePricePence) : 'Not recorded' }}</dd></div>
                      <div><dt>Source</dt><dd>{{ sourceLabel(view.purchaseSource) }}</dd></div>
                      <div><dt>Status</dt><dd>{{ ownershipLabel(view) }}</dd></div>
                      <div *ngIf="view.statusExpiryDate"><dt>Certificate expiry</dt><dd>{{ asDate(view.statusExpiryDate) | date: 'd MMMM y' }}</dd></div>
                      <div><dt>Vehicle</dt><dd>{{ view.vehicleMake || view.vehicleModel ? ((view.vehicleMake || '') + ' ' + (view.vehicleModel || '') + (view.vehicleYear ? ', ' + view.vehicleYear : '')) : 'Not recorded' }}</dd></div>
                      <div><dt>Planning to sell</dt><dd>{{ view.sellIntent === 'no' ? 'No' : view.sellIntent === 'maybe' ? 'Maybe' : 'Yes' }}</dd></div>
                    </dl>

                    <div class="editform" *ngIf="editingPurchase && editDraft">
                      <div class="fgrid">
                        <div class="fld"><label>Status</label>
                          <select [(ngModel)]="editDraft.ownershipStatus">
                            <option value="assigned">Assigned to a vehicle</option>
                            <option value="v750">On a V750 Certificate</option>
                            <option value="v778">On a V778 Retention Document</option>
                            <option value="in_transfer">In transfer</option>
                            <option value="unsure">Unsure</option>
                          </select>
                        </div>
                        <div class="fld" *ngIf="editDraft.ownershipStatus === 'v750' || editDraft.ownershipStatus === 'v778'">
                          <label>Certificate expiry</label><input type="date" [(ngModel)]="editDraft.statusExpiryDate" />
                        </div>
                        <div class="fld"><label>Purchase price (&pound;)</label><input type="text" [(ngModel)]="editDraft.purchasePounds" /></div>
                        <div class="fld"><label>Purchase date</label><input type="date" [(ngModel)]="editDraft.purchaseDate" /></div>
                        <div class="fld"><label>Purchased from</label>
                          <select [(ngModel)]="editDraft.purchaseSource">
                            <option value="dvla">DVLA</option><option value="dealer">Dealer</option>
                            <option value="private">Private seller</option><option value="auction">Auction</option>
                            <option value="gift">Gift</option><option value="unknown">Unknown</option>
                          </select>
                        </div>
                        <div class="fld"><label>Planning to sell</label>
                          <select [(ngModel)]="editDraft.sellIntent">
                            <option value="no">No</option><option value="maybe">Maybe</option><option value="yes">Yes</option>
                          </select>
                        </div>
                        <div class="fld"><label>Vehicle make</label><input type="text" [(ngModel)]="editDraft.vehicleMake" /></div>
                        <div class="fld"><label>Vehicle model</label><input type="text" [(ngModel)]="editDraft.vehicleModel" /></div>
                        <div class="fld"><label>Vehicle year</label><input type="text" [(ngModel)]="editDraft.vehicleYear" /></div>
                      </div>
                      <div class="fnav">
                        <button class="av2-btn av2-btn--green av2-btn--sm" [disabled]="editSaving" (click)="saveEditPurchase(view)">Save changes</button>
                        <button class="av2-btn av2-btn--ghost av2-btn--sm" (click)="editingPurchase = false">Cancel</button>
                      </div>
                    </div>

                    <p class="privnote">These details are private to your account. They are never used in PlateX market data, comparables or public pages.</p>
                    <div class="detacts">
                      <a class="av2-btn av2-btn--ghost av2-btn--sm" [routerLink]="plateRoute(view.registration)">View public plate page</a>
                      <ng-container *ngIf="confirmKey === 'reg-' + view.id; else regRemoveAsk">
                        <button class="linkish neg" (click)="removeRegistration(view)">Confirm removal?</button>
                        <button class="linkish quietlink" (click)="cancelConfirm()">Cancel</button>
                      </ng-container>
                      <ng-template #regRemoveAsk>
                        <button class="linkish quietlink" (click)="askConfirm('reg-' + view.id)">Remove from My Registrations</button>
                      </ng-template>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="empty" *ngIf="registrations.length === 0">
              <p>Add the registrations you own and PlateX keeps a private record of them, with market estimates to follow.</p>
            </div>

            <!-- Add-registration flow (§4) -->
            <div class="flow">
              <ng-container [ngSwitch]="flowStep">
                <div class="fstep" *ngSwitchCase="'s1'">
                  <p class="flabel">Add a registration</p>
                  <input class="regin" type="text" placeholder="e.g. OO69 HUE" aria-label="Registration" [(ngModel)]="flowReg" />
                  <h3>What best describes this registration?</h3>
                  <div class="opts">
                    <label><input type="radio" name="afp" value="own" [(ngModel)]="flowPath" /> I own it</label>
                    <label><input type="radio" name="afp" value="considering" [(ngModel)]="flowPath" /> I'm considering buying it</label>
                    <label><input type="radio" name="afp" value="tracking" [(ngModel)]="flowPath" /> I'm tracking it</label>
                  </div>
                  <p class="flowerr" *ngIf="flowError">{{ flowError }}</p>
                  <div class="fnav"><button class="av2-btn av2-btn--green av2-btn--sm" [disabled]="flowSaving" (click)="flowNext()">Continue</button></div>
                </div>

                <div class="fstep" *ngSwitchCase="'s2'">
                  <p class="flabel">Ownership &middot; you can change this later</p>
                  <h3>Where is the registration today?</h3>
                  <div class="opts">
                    <label><input type="radio" name="afs" value="assigned" [(ngModel)]="flowOwnership" /> Assigned to a vehicle</label>
                    <label><input type="radio" name="afs" value="v750" [(ngModel)]="flowOwnership" /> On a V750 Certificate of Entitlement</label>
                    <label><input type="radio" name="afs" value="v778" [(ngModel)]="flowOwnership" /> On a V778 Retention Document</label>
                    <label><input type="radio" name="afs" value="in_transfer" [(ngModel)]="flowOwnership" /> In transfer</label>
                    <label><input type="radio" name="afs" value="unsure" [(ngModel)]="flowOwnership" /> Unsure</label>
                  </div>
                  <div class="fgrid" *ngIf="flowOwnership === 'v750' || flowOwnership === 'v778'">
                    <div class="fld"><label>Certificate expiry (optional)</label><input type="date" [(ngModel)]="flowExpiry" /></div>
                  </div>
                  <div class="fnav">
                    <button class="av2-btn av2-btn--ghost av2-btn--sm" (click)="flowBack()">Back</button>
                    <button class="av2-btn av2-btn--green av2-btn--sm" (click)="flowNext()">Continue</button>
                  </div>
                </div>

                <div class="fstep" *ngSwitchCase="'s3'">
                  <p class="flabel">Purchase information &middot; optional</p>
                  <h3>How did you come to own it?</h3>
                  <div class="fgrid">
                    <div class="fld"><label>Purchase price (&pound;)</label><input type="text" [(ngModel)]="flowPricePounds" /></div>
                    <div class="fld"><label>Purchase date</label><input type="date" [(ngModel)]="flowDate" /></div>
                    <div class="fld"><label>Purchased from</label>
                      <select [(ngModel)]="flowSource">
                        <option value="dvla">DVLA</option><option value="dealer">Dealer</option>
                        <option value="private">Private seller</option><option value="auction">Auction</option>
                        <option value="gift">Gift</option><option value="unknown">Unknown</option>
                      </select>
                    </div>
                  </div>
                  <p class="fpriv">This information is private and is only used for your personal portfolio.</p>
                  <div class="fnav">
                    <button class="av2-btn av2-btn--ghost av2-btn--sm" (click)="flowBack()">Back</button>
                    <button class="av2-btn av2-btn--green av2-btn--sm" (click)="flowNext()">Continue</button>
                    <button class="linkish skip" (click)="flowPricePounds = ''; flowDate = null; flowSource = 'unknown'; flowNext()">Skip for now</button>
                  </div>
                </div>

                <div class="fstep" *ngSwitchCase="'s4'">
                  <p class="flabel">Vehicle &middot; optional</p>
                  <h3>Is it on a vehicle you'd like to record?</h3>
                  <div class="fgrid">
                    <div class="fld"><label>Make</label><input type="text" [(ngModel)]="flowMake" /></div>
                    <div class="fld"><label>Model</label><input type="text" [(ngModel)]="flowModel" /></div>
                    <div class="fld"><label>Year</label><input type="text" [(ngModel)]="flowYear" /></div>
                  </div>
                  <div class="fnav">
                    <button class="av2-btn av2-btn--ghost av2-btn--sm" (click)="flowBack()">Back</button>
                    <button class="av2-btn av2-btn--green av2-btn--sm" (click)="flowNext()">Continue</button>
                    <button class="linkish skip" (click)="flowMake = ''; flowModel = ''; flowYear = ''; flowNext()">Skip for now</button>
                  </div>
                </div>

                <div class="fstep" *ngSwitchCase="'s5'">
                  <p class="flabel">One last question</p>
                  <h3>Are you planning to sell this registration?</h3>
                  <div class="opts">
                    <label><input type="radio" name="afsell" value="no" [(ngModel)]="flowSell" /> No</label>
                    <label><input type="radio" name="afsell" value="maybe" [(ngModel)]="flowSell" /> Maybe</label>
                    <label><input type="radio" name="afsell" value="yes" [(ngModel)]="flowSell" /> Yes</label>
                  </div>
                  <p class="fpriv">This doesn't change anything today.</p>
                  <p class="flowerr" *ngIf="flowError">{{ flowError }}</p>
                  <div class="fnav">
                    <button class="av2-btn av2-btn--ghost av2-btn--sm" (click)="flowBack()">Back</button>
                    <button class="av2-btn av2-btn--green av2-btn--sm" [disabled]="flowSaving" (click)="flowNext()">Finish</button>
                  </div>
                </div>

                <div class="fstep done" *ngSwitchCase="'done'">
                  <p class="flabel">Done</p>
                  <span class="av2-plate plate-font md">{{ formatReg(flowDoneReg) }}</span>
                  <p>{{ flowDoneMessage }}</p>
                  <div class="fnav">
                    <button class="av2-btn av2-btn--green av2-btn--sm" (click)="flowView()">View it</button>
                    <button class="av2-btn av2-btn--ghost av2-btn--sm" (click)="flowReset()">Add another</button>
                  </div>
                </div>
              </ng-container>
            </div>
          </ng-container>
        </section>

        <!-- ---------------- RECENTLY VIEWED ---------------- -->
        <section class="pane" *ngSwitchCase="'recent'">
          <div class="pane-hd">
            <p class="eyebrow">Recently viewed</p>
            <h1 class="stmt">Pick up where you left off.</h1>
            <p class="lead">Registrations you've explored recently, newest first. Your viewing history is private to your account.</p>
          </div>

          <div class="skeleton" *ngIf="recentState === 'loading'" aria-label="Loading recently viewed">
            <div class="sk-line"></div><div class="sk-line"></div><div class="sk-line short"></div>
          </div>

          <div class="loaderr" role="alert" *ngIf="recentState === 'error'">
            <p>We couldn't load your viewing history just now.</p>
            <button class="av2-btn av2-btn--ghost av2-btn--sm" (click)="loadRecent()">Try again</button>
          </div>

          <ng-container *ngIf="recentState === 'ready'">
            <div class="av2-rows" *ngIf="recent.length > 0">
              <div class="av2-row read" *ngFor="let view of recent">
                <span class="av2-dot"></span>
                <div>
                  <div class="t"><span class="av2-plate plate-font sm">{{ formatReg(view.registration) }}</span></div>
                </div>
                <div class="side">
                  <span>{{ asDate(view.viewedAt) | relativeTime }}</span>
                  <a class="linkish" [routerLink]="plateRoute(view.registration)">View plate</a>
                </div>
              </div>
            </div>

            <p class="footnote" *ngIf="recent.length > 0">
              History is kept for 90 days &middot;
              <ng-container *ngIf="confirmKey === 'clear-recent'; else clearAsk">
                <button class="linkish neg" (click)="clearRecent()">Confirm clear?</button>
                <button class="linkish quietlink" (click)="cancelConfirm()">Cancel</button>
              </ng-container>
              <ng-template #clearAsk>
                <button class="linkish quietlink" (click)="askConfirm('clear-recent')">Clear history</button>
              </ng-template>
            </p>

            <div class="empty" *ngIf="recent.length === 0">
              <p>No viewing history yet. Plate pages you open will be listed here, kept for 90 days, and can be cleared at any time.</p>
            </div>
          </ng-container>
        </section>

        <!-- ---------------- UPDATES ---------------- -->
        <section class="pane" *ngSwitchCase="'updates'">
          <div class="pane-hd">
            <p class="eyebrow">Updates</p>
            <h1 class="stmt">{{ marketEvents ? 'What changed, and why it matters.' : 'Your alerts, in one list.' }}</h1>
            <p class="lead">Every update comes from a search or a plate you follow. Nothing else lands here.</p>
          </div>

          <div class="filters" role="tablist" aria-label="Filter updates">
            <button [class.on]="updateFilter === 'all'" (click)="setUpdateFilter('all')">All</button>
            <button [class.on]="updateFilter === 'saved_search'" (click)="setUpdateFilter('saved_search')">Saved searches</button>
            <button [class.on]="updateFilter === 'followed_plate'" (click)="setUpdateFilter('followed_plate')">Tracked plates</button>
          </div>

          <div class="skeleton" *ngIf="updatesState === 'loading'" aria-label="Loading updates">
            <div class="sk-line"></div><div class="sk-line"></div><div class="sk-line short"></div>
          </div>

          <div class="loaderr" role="alert" *ngIf="updatesState === 'error'">
            <p>We couldn't load your updates just now.</p>
            <button class="av2-btn av2-btn--ghost av2-btn--sm" (click)="loadUpdates()">Try again</button>
          </div>

          <ng-container *ngIf="updatesState === 'ready'">
            <div class="av2-rows" *ngIf="updates.length > 0">
              <div class="av2-row" [class.read]="item.readState !== 'new'" *ngFor="let item of updates" (click)="markRead(item)">
                <span class="av2-dot"></span>
                <div>
                  <div class="t">
                    <span class="av2-plate plate-font sm" *ngIf="item.registration">{{ formatReg(item.registration) }}</span>
                    {{ updateTitle(item) }} <span class="av2-chip est" *ngIf="item.provisional">Provisional</span>
                  </div>
                  <div class="d" *ngIf="updateDetail(item)">{{ updateDetail(item) }}</div>
                  <div class="cause">{{ causeLabel(item) }}</div>
                </div>
                <div class="side">
                  <span>{{ asDate(item.createdAt) | relativeTime }}</span>
                  <a class="linkish" [routerLink]="plateRoute(item.registration)" *ngIf="item.registration">View plate</a>
                </div>
              </div>
            </div>
            <p class="footnote" *ngIf="updates.length > 0">{{ marketEvents ? 'Click an update to mark it read. ' : '' }}Updates are kept for 90 days.</p>

            <div class="empty" *ngIf="updates.length === 0">
              <p *ngIf="marketEvents">Nothing yet. When a tracked plate changes or a saved search finds something new, it will be listed here.</p>
              <p *ngIf="!marketEvents">Nothing yet. When a tracked plate or a plate matching a saved search is listed for sale, the alert will be listed here too.</p>
            </div>
          </ng-container>
        </section>

        <!-- ---------------- SETTINGS ---------------- -->
        <section class="pane" *ngSwitchCase="'settings'">
          <div class="pane-hd">
            <p class="eyebrow">Settings</p>
            <h1 class="stmt">Profile &amp; preferences.</h1>
            <p class="lead">Who you are, and how PlateX is allowed to contact you.</p>
          </div>

          <div class="setcard">
            <h3>Profile</h3>

            <ng-container [ngSwitch]="profileState">
              <div *ngSwitchCase="'loading'" class="skeleton" aria-label="Loading profile">
                <div class="sk-line"></div>
                <div class="sk-line"></div>
                <div class="sk-line short"></div>
              </div>

              <div *ngSwitchCase="'error'" class="loaderr" role="alert">
                <p>We couldn't load your profile just now.</p>
                <button class="av2-btn av2-btn--ghost av2-btn--sm" (click)="loadProfile()">Try again</button>
              </div>

              <ng-container *ngSwitchCase="'ready'">
                <div class="r"><span>Name</span><b>{{ fullName || 'Not set' }}</b></div>
                <div class="r"><span>Email</span><b>{{ userDetails?.email || 'Not set' }}</b></div>
                <div class="r"><span>Contact details</span><a class="linkish" routerLink="/account/details">Manage</a></div>
              </ng-container>
            </ng-container>
          </div>

          <div class="setcard">
            <h3>Manage alerts</h3>
            <p>Alerts are set on each tracked plate and saved search. Saved plates never send alerts.</p>
            <div class="r count"><span>Tracked plates</span><b>{{ summary ? summary.followedPlates : '–' }}</b><a class="linkish" routerLink="/account/followed">Manage</a></div>
            <div class="r count"><span>Saved searches</span><b>{{ summary ? summary.savedSearches : '–' }}</b><a class="linkish" routerLink="/account/searches">Manage</a></div>
            <div class="r count"><span>Saved plates (no alerts)</span><b>{{ summary ? (summary.savedPlates ?? 0) : '–' }}</b><a class="linkish" routerLink="/account/saved">View</a></div>
          </div>

          <div class="setcard">
            <h3>Leave PlateX</h3>
            <p>Delete your account and everything in it. This can't be undone.</p>
            <div class="r end"><span></span><a class="linkish neg" routerLink="/data-deletion">Request deletion</a></div>
          </div>
        </section>

        <!-- ---------------- ADMIN (admins only) ---------------- -->
        <section class="pane pane--admin" *ngSwitchCase="'admin'">
          <div class="pane-hd">
            <p class="eyebrow">Admin</p>
            <h1 class="stmt">The workbench.</h1>
            <p class="lead">Internal tools. Only PlateX administrators can see this section; every tool re-verifies on the server.</p>
          </div>
          <div class="admin-host" *ngIf="isAdmin">
            <pla-admin-page></pla-admin-page>
          </div>
        </section>

      </ng-container>
    </main>
  </div>
</div>
